// Coldroom register row shape + the delivery-bins mapper — pure, extracted
// from ColdroomReport.tsx so the delivery mapping is unit-testable (spec:
// docs/specs/delivery-coldroom-placement.md). The pallet/bin mappers stay in
// the page (they lean on its aging/grade helpers); this is the shared shape
// they all normalise into.

export interface ColdRow {
  kind:        'Pallet' | 'Bin' | 'Delivery'
  // IDENTITY — the primary key, and the ONLY thing that may be used as one:
  // the React key and the CSV's ID column. Never render it; render `display`.
  // Only pallets have a human-readable pk (pallets.pallet_id TEXT, e.g.
  // 'KF49-2026-09461'); bins and deliveries are UUID pks (gen_random_uuid(),
  // 36 chars), which is why identity and display had to be separated.
  id:          string
  // DISPLAY — the operator-facing reference for the ID column, one per kind:
  //   Pallet   → pallet_id, whole and exact (it IS the reference on the label)
  //   Bin      → bins.bin_number ('RBN-2026-00001'), the number painted on the bin
  //   Delivery → the PUC, or an em-dash when the delivery carries none
  // Never a UUID and never a slice of one. A 36-char hex string in a cell that
  // is font-mono/whitespace-nowrap would blow the print layout apart, and a
  // truncated fragment of a UUID identifies nothing an operator can act on —
  // an em-dash is the more honest answer where no reference exists, the same
  // discipline the SSCC column follows.
  display:     string
  // The SSCC captured against an EXTERNALLY-RECEIVED pallet — pallets.sscc,
  // written by receive_packed_pallets (mig 20260807090000) and
  // update_received_pallet (mig 20260813090000). NOT pallets.ssc_id, which is
  // the barcode our own packing line mints; the two lanes are disjoint (zero
  // rows carry both). Bins and deliveries have no SSCC, so they carry null and
  // render an em-dash.
  sscc:        string | null
  // THE ARRIVAL DOCUMENT, for a pallet that was RECEIVED rather than packed
  // here (mig 20260807090000). Two numbers, because the two sides of the
  // conversation quote different ones:
  //
  //   receiptNo  ours — pallet_receipts.receipt_no, 'RCV-2026-014'. What the
  //              receipt is filed under here, and what to open to see the whole
  //              arrival: the sender, the transporter, every pallet on the load.
  //   senderDoc  theirs — pallet_receipts.doc_ref, the delivery note / packing
  //              list number off the paperwork that came with the truck. Often
  //              absent: it is an optional field on the capture form, and a
  //              missing one is "not captured", never blank-because-none-exists.
  //
  // Both are null for a pallet OUR lines packed, for a loose bin and for an
  // untipped delivery — none of them arrived on a receipt — and those render an
  // em-dash, exactly as the SSCC column does for the same rows.
  receiptNo:   string | null
  /** pallet_receipts.id, to open the receipt. Null with receiptNo. */
  receiptId?:  string | null
  senderDoc:   string | null
  roomId:      string | null
  roomCode:    string
  roomSort:    number
  cell:        string
  grower:      string
  variety:     string
  detail:      string            // pack / grade / size summary
  cartons:     number | null
  // NULLABLE ON PURPOSE — an absent weight is not zero.
  //
  // pallets.net_weight_kg is genuinely nullable: mig 890 dropped its NOT NULL
  // and added `awaiting_weight BOOLEAN NOT NULL DEFAULT FALSE` for shell
  // pallets whose weight is still pending. This field used to be plain
  // `number`, and the mappers coerced with `|| 0`, so a pallet that had never
  // been weighed entered the register as 0.0 kg and was then SUMMED into the
  // Net kg KPI and the tfoot total as if it weighed nothing.
  //
  // A stored zero is unreachable, so every absent weight is a NULL and there is
  // no ambiguity to preserve: receivedPalletLines.tsx's numOrNull returns
  // `n > 0 ? n : null`, and ReceiptDetail.tsx rejects `<= 0` on every weight
  // entry path. Nothing can write 0.0 — so a 0.0 on this report was always a
  // missing weight wearing a number's clothes.
  //
  // Keep this nullable. Re-widening it to `number` is what let the loss hide:
  // the type system could never surface it.
  netKg:       number | null
  // pallets.awaiting_weight — the shell-pallet flag (mig 890), NOT NULL DEFAULT
  // FALSE, so it is a real boolean and never null. Bins and deliveries have no
  // such column and carry false: their null weights are structural, not pending.
  awaitingWeight: boolean
  placedIso:   string | null     // stocked_at / coldroom_placed_at (placement into cold)
  ageStartIso: string            // effective age anchor
  isExport:    boolean
  cost:        number | null     // pallets only (bins + deliveries bill separately)
  deliveryBins: number | null    // 'Delivery' rows only — bins_remaining at read time
}

// A delivery whose remaining untipped bins are recorded as standing in a
// coldroom (deliveries.coldroom_id, mig 20260728140000).
export interface PlacedDeliveryRow {
  delivery_id:        string
  coldroom_id:        string | null
  grower:             string | null
  variety:            string | null
  puc:                string | null
  bins_remaining:     number
  received_at:        string
  coldroom_placed_at: string | null
}

export interface RoomRef {
  code:       string
  sort_order: number
}

// Which room an active cooling batch occupies (FC-20). The batch's own room
// column wins — cooling_coldroom_id while COOLING (per-batch force-cool room,
// mig 20260728173529: set exactly when a lane has ≥2 allocated coolers, i.e.
// when the stage mapping below has no single answer), holding_coldroom_id
// while IN_HOLDING — falling back to the per-tenant stage mapping, which the
// report collapses to a room only when every channel agrees on ONE room for
// the stage. null → the report's explicit 'Unmapped' bucket.
export interface StageRoomIds { COOLING?: string; IN_HOLDING?: string }

export interface BatchRoomFields {
  status:              'COOLING' | 'IN_HOLDING' | 'RELEASED'
  cooling_coldroom_id: string | null
  holding_coldroom_id: string | null
}

export function batchRoomId(b: BatchRoomFields, stageRoomIds: StageRoomIds): string | null {
  return b.status === 'COOLING'
    ? b.cooling_coldroom_id ?? stageRoomIds.COOLING ?? null
    : b.holding_coldroom_id ?? stageRoomIds.IN_HOLDING ?? null
}

// The dollies standing in ONE room, for the Coldroom map's "+ N dollies" card
// (F05 UF-06). Same room rule as the register's dolly rows (batchRoomId), so
// the map and the register count the same dollies. A dolly has no cell — no
// screen records a holding position — so this is a room-level count only.
export interface RoomDollies { total: number; cooling: number; holding: number; released: number }

export function dolliesInRoom(
  batches: readonly BatchRoomFields[], stageRoomIds: StageRoomIds, roomId: string,
): RoomDollies {
  const out: RoomDollies = { total: 0, cooling: 0, holding: 0, released: 0 }
  for (const b of batches) {
    if (batchRoomId(b, stageRoomIds) !== roomId) continue
    out.total += 1
    if (b.status === 'COOLING') out.cooling += 1
    else if (b.status === 'RELEASED') out.released += 1
    else out.holding += 1
  }
  return out
}

// "4 in holding, 2 cooling, 1 released" — the stages present, holding first (a
// holding room is where most of a room's dollies wait), never "0 cooling".
// Released dollies are counted too (Ruled 8 Oct, as the mock-up).
export function dollyStageBreakdown(d: RoomDollies): string {
  const parts: string[] = []
  if (d.holding > 0) parts.push(`${d.holding} in holding`)
  if (d.cooling > 0) parts.push(`${d.cooling} cooling`)
  if (d.released > 0) parts.push(`${d.released} released`)
  return parts.join(', ')
}

// Delivery bins are bulk stock: room-level only (cell —), no per-bin net
// weight, and they age from intake (received_at) — the same FIFO concern the
// Yard map tracks — not from cold placement.
export function deliveryToColdRow(d: PlacedDeliveryRow, roomById: Map<string, RoomRef>): ColdRow {
  const room = d.coldroom_id ? roomById.get(d.coldroom_id) : null
  return {
    kind:        'Delivery',
    id:          d.delivery_id,
    // delivery_id is a UUID, so the PUC is the only operator-facing reference a
    // placed delivery has. Where there is none, an em-dash — the Detail column
    // still carries "N bins · untipped delivery" to identify the row by.
    display:     d.puc ?? '—',
    sscc:        null,           // bulk bins on a delivery note — no SSCC exists
    // A grower delivery of untipped bins is not a pallet receipt: it has a
    // delivery note of its own, on a different table, and printing that here
    // would put two different documents in one column.
    receiptNo:   null,
    senderDoc:   null,
    roomId:      d.coldroom_id,
    roomCode:    room?.code ?? '—',
    roomSort:    room?.sort_order ?? 9_999,
    cell:        '—',
    grower:      d.grower ?? '—',
    variety:     d.variety ?? '—',
    detail:      [`${d.bins_remaining} bins`, d.puc ? `PUC ${d.puc}` : null, 'untipped delivery']
                   .filter(Boolean).join(' · '),
    cartons:     null,
    // null, not 0 — matching the `cartons`/`cost` nulls either side of it. A
    // delivery is untipped bulk bins on a note: no per-bin weight has ever been
    // captured, so the register has nothing to report. This literal used to be a
    // hardcoded `0`, which the Net kg total then added up as real weight.
    netKg:       null,
    awaitingWeight: false,   // structural, not pending — no delivery ever carries a per-bin weight
    placedIso:   d.coldroom_placed_at,
    ageStartIso: d.received_at,
    isExport:    false,
    cost:        null,
    deliveryBins: d.bins_remaining,
  }
}

// ---------------------------------------------------------------------------
// Net-kg display — the single place that decides how an absent weight reads.
//
// Deliberately exported rather than inlined at the call sites: PR #1662 rewrites
// this report's cells into a generated column table
// (src/lib/coldroomReportColumns.ts) and merges after this change. It must call
// these, not re-type a coerced `.toFixed(1)` into its column definitions — that
// would silently restore the zeros this change removed.
// ---------------------------------------------------------------------------

/** On screen: an em-dash for an absent weight — the house rendering for
 *  "nothing captured" (DeliveryIntakeWeights.tsx does `… != null ? … : '—'`). */
export function fmtNetKg(netKg: number | null): string {
  return netKg != null ? netKg.toFixed(1) : '—'
}

/** In CSV: an EMPTY cell, never '0.0' — the same treatment `cartons` already
 *  gets in csvCells (`d.row.cartons ?? ''`). A spreadsheet SUM must not be
 *  handed a zero the packhouse never weighed. */
export function csvNetKg(netKg: number | null): string {
  return netKg != null ? netKg.toFixed(1) : ''
}

/** The disclosure appended to the Net kg total: what the figure LEFT OUT.
 *
 *  Empty string when nothing was excluded, so a fully-weighed register reads
 *  exactly as it did before. Named by kind when the excluded rows are all
 *  pallets (the ordinary case — awaiting_weight is a pallet concept), and
 *  falls back to the register's own neutral noun once a bin is in the set, so
 *  the sentence is never more specific than the truth.
 *
 *  Deliveries are NOT counted here: a delivery is untipped bulk with no per-bin
 *  weight by design, and the totals line already discloses it as "N delivery
 *  bins". Counting it as "not yet weighed" would invent a pending task. */
export function unweighedNote(pallets: number, bins: number): string {
  const n = pallets + bins
  if (n <= 0) return ''
  const noun = bins === 0 ? (n === 1 ? 'pallet' : 'pallets') : (n === 1 ? 'item' : 'items')
  return `${n} ${noun} not yet weighed`
}
